import React, {
  useCallback,
  useEffect,
  useState,
  useRef,
  useMemo,
} from 'react';

import { useRouter } from 'next/router';
import { MdAdd, MdRemove } from 'react-icons/md';
import styled from 'styled-components';

import ColorWheel from '~/shared/components/ColorWheel';
import useColorActions from '~/shared/utils/useColorActions';
import {
  Collapsible,
  useProductFilters,
  useProductFiltersCustomFilters,
  desktop,
  mobile,
  useProductBrowser,
  useBreakpoint,
} from '~/techstyle-shared/react-components';
import { FormattedMessage } from '~/techstyle-shared/react-intl';

import { useProductBrowserFilterState } from '../ProductBrowserFilterContext';

const FilterWrapper = styled.div`
  padding: 2px 0;
  box-shadow: inset 0 1px 0 0 #eee;

  ${({ theme }) => theme.gridFilterStyles.listingFilterStyle}

  :last-child {
    box-shadow:
      0 0 0 0 ${({ theme }) => theme.colors.desktopProductListingFilter},
      0 -1px 0 0 ${({ theme }) => theme.colors.desktopProductListingFilter};
  }
`;

const FilterFieldButton = styled(Collapsible.Button)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 19px 16px;
  text-align: left;

  ${mobile`
    padding: 12px 24px;
  `}

  ${desktop`
    padding: 12px 8px;
  `}

  svg {
    margin -5px 0;
    ${({ theme }) => theme.gridFilterStyles.filterFieldButtonSvgStyle}
  }
`;

const FilterFieldLabel = styled.span`
  font-size: 16px;
  color: #333;
  font-weight: 600;

  ${mobile`
    font-size: 14px;
  `}

  ${desktop`
    font-size: 14px;
  `}
`;

const ToggleIcon = styled.svg`
  flex: 0 0 auto;
`;

const ColorWheelContainer = styled.div`
  margin: 24px 0 16px;
`;

function ColorWheelFilter() {
  const { baseFilters, filters, setFilters } = useProductBrowser();
  const { isMobile } = useBreakpoint();
  const [color, setColor] = useState(null);
  const [colorTags, setColorTags] = useState([]);
  const colorActions = useColorActions();
  const { query } = useRouter();
  const browserFilterState = useProductBrowserFilterState();

  // Operation status ref prevents race conditions ('idle' | 'updating' | 'cleaning')
  const operationStatusRef = useRef('idle');

  // Refs for managing color state and preventing race conditions
  const colorForTagsRef = useRef(null); // Color corresponding to current colorTags
  const appliedColorTagsRef = useRef(null); // Tag IDs applied to filters (for removal)
  const colorTagsRef = useRef([]); // Synchronous copy of colorTags state
  const updateFiltersRef = useRef(null); // Stable reference to filter update function

  // Check both contexts for backwards compatibility
  const productFilters = useProductFilters();
  const customFilters = useProductFiltersCustomFilters();
  const filterContext = useMemo(() => {
    return productFilters || customFilters;
  }, [productFilters, customFilters]);

  // Mobile: deferred application via filterContext, Desktop: immediate via ProductBrowser
  const isMobileFilterMode = isMobile && !!filterContext;
  const filterSettings = filterContext?.filterSettings;
  const currentFilters = isMobileFilterMode
    ? filterContext.selectedFilters
    : filters;
  const updateFilters = isMobileFilterMode
    ? filterContext.setSelectedFilters
    : setFilters;

  updateFiltersRef.current = updateFilters;

  const baseFiltersTagIds = useMemo(
    () => baseFilters?.tagIds || [],
    [baseFilters]
  );

  // Ensure all filter fields exist in aggregationFilter (mobile only, prevents errors)
  const ensureAllFilterFields = useCallback(
    (aggregationFilter, filterSettingsArray) => {
      if (
        !isMobileFilterMode ||
        !filterSettingsArray ||
        filterSettingsArray.length === 0
      ) {
        return aggregationFilter;
      }

      const result = { ...aggregationFilter };
      filterSettingsArray.forEach((filterSetting) => {
        if (filterSetting.field && !result[filterSetting.field]) {
          result[filterSetting.field] = [];
        }
      });
      return result;
    },
    [isMobileFilterMode]
  );

  // Build cleaned filters by removing custom_color and associated tagIds
  const buildCleanedFilters = useCallback(
    (prevFilters, colorTagsSet, filterSettings) => {
      const cleanedAggregations = (prevFilters.aggregations || []).filter(
        (agg) => agg.field !== 'custom_color'
      );

      const baseCleanedAggregationFilter = { ...prevFilters.aggregationFilter };
      delete baseCleanedAggregationFilter.custom_color;

      // Exclude custom_color to prevent it being re-added as an empty array
      const filterSettingsWithoutColor =
        filterSettings?.filter((fs) => fs.field !== 'custom_color') || [];
      const cleanedAggregationFilter = ensureAllFilterFields(
        baseCleanedAggregationFilter,
        filterSettingsWithoutColor
      );

      const prevTagIds = prevFilters.tagIds || [];
      let cleanedTagIds;
      if (colorTagsSet.size > 0) {
        cleanedTagIds = prevTagIds
          .map((tagGroup) =>
            Array.isArray(tagGroup)
              ? tagGroup.filter((tagId) => !colorTagsSet.has(tagId))
              : []
          )
          .filter((tagGroup) => tagGroup.length > 0);
      } else {
        cleanedTagIds = [];
      }

      const result = {
        ...prevFilters,
        aggregations: cleanedAggregations,
        aggregationFilter: cleanedAggregationFilter,
      };

      if (cleanedTagIds.length > 0) {
        result.tagIds = [...baseFiltersTagIds, ...cleanedTagIds];
      } else {
        result.tagIds = baseFiltersTagIds;
      }

      return result;
    },
    [ensureAllFilterFields, baseFiltersTagIds]
  );

  const [isOpenFromState, setIsOpen] = useState(false);
  const isOpenFromContext =
    filterContext?.expandedCollapsibleFilters['custom-color'];
  const isOpen = filterContext ? isOpenFromContext : isOpenFromState;

  const handleFilterFieldClick = useCallback(
    () =>
      filterContext
        ? filterContext.toggleCollapsibleFilterIsOpen({
            type: 'toggle',
            filterField: 'custom-color',
          })
        : setIsOpen((prevOpen) => !prevOpen),
    [filterContext]
  );

  // Extract primitive values to prevent infinite loops from filter object dependencies
  const customColorValue = currentFilters?.aggregationFilter?.custom_color?.[0];
  const currentTagIds = currentFilters?.tagIds;

  // Sync colorTagsRef with state for synchronous access
  useEffect(() => {
    colorTagsRef.current = colorTags;
  }, [colorTags]);

  // Mobile: dynamically add custom_color to filterSettings to show as a pill
  useEffect(() => {
    if (
      !isMobileFilterMode ||
      !filterContext?.setFilterSettings ||
      !filterSettings
    ) {
      return;
    }

    const hasCustomColorSetting = filterSettings.some(
      (fs) => fs.field === 'custom_color'
    );

    if (customColorValue && !hasCustomColorSetting) {
      const newFilterSettings = [
        ...filterSettings,
        {
          field: 'custom_color',
          label: 'Color',
          getCount: ({ filters }) =>
            filters.aggregationFilter?.custom_color?.length || 0,
        },
      ];
      filterContext.setFilterSettings(newFilterSettings);
    } else if (!customColorValue && hasCustomColorSetting) {
      filterContext.setFilterSettings(
        filterSettings.filter((fs) => fs.field !== 'custom_color')
      );
    }
  }, [customColorValue, filterSettings, filterContext, isMobileFilterMode]);

  // Cleanup effect: Clear color state when filter is removed externally
  useEffect(() => {
    if (operationStatusRef.current !== 'idle') {
      return;
    }

    const colorFilterRemoved = !customColorValue;

    // Check if color was previously applied (ref, tagIds, or mobile appliedFilters)
    const hadColorInRef = appliedColorTagsRef.current?.length > 0;
    const hadColorInApplied = isMobileFilterMode
      ? filterContext?.appliedFilters?.aggregationFilter?.custom_color?.[0]
      : false;
    const hadColorPreviously = hadColorInRef || hadColorInApplied;

    if (colorFilterRemoved && hadColorPreviously) {
      operationStatusRef.current = 'cleaning';

      const tagsToRemove = appliedColorTagsRef.current;

      // Clear refs immediately (before async setState) to prevent race conditions
      colorTagsRef.current = [];
      colorForTagsRef.current = null;
      setColor(null);
      setColorTags([]);

      const colorTagsSet =
        tagsToRemove && tagsToRemove.length > 0
          ? new Set(tagsToRemove)
          : new Set();

      // Desktop: remove custom_color + tagIds; Mobile: remove tagIds only (FilterTags handles custom_color)
      if (!isMobileFilterMode) {
        updateFiltersRef.current((prevFilters) => {
          return buildCleanedFilters(prevFilters, colorTagsSet, filterSettings);
        });
      } else if (
        colorTagsSet.size > 0 ||
        (currentFilters?.tagIds && currentFilters.tagIds.length > 0)
      ) {
        // Clean up on mobile if we have tags to remove OR if there are tagIds in selectedFilters
        updateFiltersRef.current((prevFilters) => {
          const prevTagIds = prevFilters.tagIds || [];
          let cleanedTagIds;
          if (colorTagsSet.size > 0) {
            cleanedTagIds = prevTagIds
              .map((tagGroup) =>
                Array.isArray(tagGroup)
                  ? tagGroup.filter((tagId) => !colorTagsSet.has(tagId))
                  : []
              )
              .filter((tagGroup) => tagGroup.length > 0);
          } else {
            cleanedTagIds = [];
          }

          const result = { ...prevFilters };
          if (cleanedTagIds.length > 0) {
            result.tagIds = [...baseFiltersTagIds, ...cleanedTagIds];
          } else {
            result.tagIds = baseFiltersTagIds;
          }

          return result;
        });
      }

      // Desktop only: clean browserFilterState and force cleanup after React updates
      if (!isMobileFilterMode) {
        // Clean browserFilterState to prevent sync issues
        if (browserFilterState?.setFilters) {
          browserFilterState.setFilters((prevSharedFilters) => {
            return buildCleanedFilters(
              prevSharedFilters,
              colorTagsSet,
              filterSettings
            );
          });
        }

        // Force cleanup after 100ms if tagIds persist
        if (currentFilters?.tagIds) {
          const tagsToCleanLater = Array.from(colorTagsSet);

          setTimeout(() => {
            const filtersToCheck = filters;

            if (filtersToCheck?.tagIds && filtersToCheck.tagIds.length > 0) {
              const tagsToCleanSet = new Set(tagsToCleanLater);
              const shouldClean =
                tagsToCleanLater.length > 0
                  ? tagsToCleanLater.some((tagId) =>
                      filtersToCheck.tagIds.some(
                        (group) => Array.isArray(group) && group.includes(tagId)
                      )
                    )
                  : true;

              if (shouldClean) {
                setFilters((prevFilters) =>
                  buildCleanedFilters(
                    prevFilters,
                    tagsToCleanSet,
                    filterSettings
                  )
                );
              }
            }
          }, 100);
        }
      }

      appliedColorTagsRef.current = null;
      operationStatusRef.current = 'idle';
    }
  }, [
    customColorValue,
    currentTagIds,
    isMobileFilterMode,
    browserFilterState,
    filters,
    setFilters,
    filterSettings,
    buildCleanedFilters,
    currentFilters,
    filterContext,
    baseFiltersTagIds,
  ]);

  // Application effect: Apply color filter when colorTags changes
  useEffect(() => {
    if (operationStatusRef.current !== 'idle') {
      return;
    }

    const colorToUse = colorForTagsRef.current || color;

    // Check ref instead of state to avoid race condition during cleanup
    if (colorTagsRef.current.length > 0 && colorToUse) {
      const newCustomColor = Object.values(colorToUse).join(',');

      // Skip if color hasn't changed or wrong update function on mobile
      if (customColorValue === newCustomColor) {
        return;
      }

      if (isMobileFilterMode && updateFilters === setFilters) {
        return;
      }

      operationStatusRef.current = 'updating';

      const upsertAggregation = (aggs = [], nextAgg) => {
        const idx = aggs.findIndex((a) => a.field === nextAgg.field);
        if (idx === -1) {
          return [...aggs, nextAgg];
        }
        const copy = aggs.slice();
        copy[idx] = { ...aggs[idx], ...nextAgg };
        return copy;
      };

      // Merge new color tags with existing tagIds (remove previous color tags first)
      const existingTagIds = currentTagIds;
      let newTagIds;

      if (!existingTagIds || existingTagIds.length === 0) {
        newTagIds = [...baseFiltersTagIds, [...colorTags]];
      } else {
        const previouslyAppliedColorTags = appliedColorTagsRef.current || [];
        const previousColorTagsSet = new Set(previouslyAppliedColorTags);

        // Safety: if cleanup incomplete, replace all tagIds
        if (
          previouslyAppliedColorTags.length === 0 &&
          existingTagIds.length > 0
        ) {
          newTagIds = [...baseFiltersTagIds, [...colorTags]];
        } else {
          const filteredTagGroups = existingTagIds
            .map((tagGroup) => {
              if (!Array.isArray(tagGroup)) {
                return [];
              }
              return tagGroup.filter(
                (tagId) => !previousColorTagsSet.has(tagId)
              );
            })
            .filter((tagGroup) => tagGroup.length > 0);

          newTagIds = [
            ...baseFiltersTagIds,
            ...filteredTagGroups,
            [...colorTags],
          ];
        }
      }

      appliedColorTagsRef.current = [...colorTags];

      updateFiltersRef.current((prevFilters) => {
        const baseAggregationFilter = {
          ...(prevFilters.aggregationFilter || {}),
          custom_color: [newCustomColor],
        };

        const completeAggregationFilter = ensureAllFilterFields(
          baseAggregationFilter,
          filterContext?.filterSettings
        );

        return {
          ...prevFilters,
          tagIds: newTagIds,
          aggregations: upsertAggregation(prevFilters.aggregations || [], {
            field: 'custom_color',
            size: 1,
          }),
          aggregationFilter: completeAggregationFilter,
        };
      });

      operationStatusRef.current = 'idle';
    }
  }, [
    colorTags,
    color,
    currentTagIds,
    customColorValue,
    isMobileFilterMode,
    setFilters,
    ensureAllFilterFields,
    filterSettings,
    updateFilters,
    filterContext,
    baseFiltersTagIds,
  ]);

  // Fetch nearest color tags when color changes
  useEffect(() => {
    if (!color) {
      return;
    }

    let cancelled = false;
    const colorValue = color; // Capture current color value

    const fetchColorTags = async () => {
      const nearestColors =
        await colorActions.getNearestColorTagsByColorValues(colorValue);

      // Only update if this request wasn't cancelled
      if (!cancelled && nearestColors?.payload) {
        const tags = nearestColors.payload.map((c) => c.tagId);
        setColorTags(tags);
        // Track which color these tags correspond to
        colorForTagsRef.current = colorValue;
      }
    };

    fetchColorTags();

    // Cleanup function to cancel stale requests
    return () => {
      cancelled = true;
    };
  }, [color, colorActions]);

  useEffect(() => {
    // Read initial color from URL on mount
    const urlParams = new URLSearchParams(query);
    const customColorParam = urlParams.get('custom_color');
    if (customColorParam) {
      const colorValues = customColorParam.split(',').map(Number);
      setColor({
        r: colorValues[0],
        g: colorValues[1],
        b: colorValues[2],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount, intentionally ignoring query changes

  return (
    <FilterWrapper>
      <Collapsible isOpen={isOpen} defaultOpen={false}>
        <FilterFieldButton
          onClick={handleFilterFieldClick}
          data-autotag="filter-custom-color"
        >
          <FilterFieldLabel>
            <FormattedMessage
              id="color_wheel_filter.label"
              defaultMessage="Color"
            />
          </FilterFieldLabel>
          {isOpen ? (
            <ToggleIcon
              as={MdRemove}
              data-autotag="filter-custom-color-collapse"
            />
          ) : (
            <ToggleIcon as={MdAdd} data-autotag="filter-custom-color-expand" />
          )}
        </FilterFieldButton>
        <Collapsible.Content mountOnEnter unmountOnExit>
          <ColorWheelContainer>
            <ColorWheel lightness={50} onColorSelect={(rgb) => setColor(rgb)} />
          </ColorWheelContainer>
        </Collapsible.Content>
      </Collapsible>
    </FilterWrapper>
  );
}

export default ColorWheelFilter;
